import React from 'react';

import { FormikProps } from 'formik';
import Tooltip from 'antd/es/tooltip';
import Switch from 'antd/es/switch';
import Select from 'antd/es/select';

import loadable from '@common/react/loadable/loadableSettings';
import File from '@common/react/components/Forms/Files/File';
import Colorbox from '@common/react/components/UI/Colorbox/Colorbox';
import { removeFromArrayByIndex } from '@common/typescript/utils/immutability';
import { File as FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import FormikField from '@common/react/components/Formik/advanced/FormikField';
import Button from '@common/react/components/Forms/Button';
import ReadOnlyControlValue from '@common/react/components/UI/ReadOnlyControlValue/ReadOnlyControlValue';

import { Feature } from '@commonTuna/react/objects/CompanyFeature';

import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import { extensions } from '@app/components/UI/SendEmailModal/SendEmailReadonly';
import { useCompanySettingsContext } from '@app/components/UI/CompanySettingsSetter/CompanySettingsSetter';
import { AppointmentEmail } from '@app/objects/AppointmentEmail';

const Wysiwyg = loadable(() =>
	import(/* webpackChunkName: "WysiwygComponentPage" */ '@common/react/components/Forms/Wysiwyg/WysiwygWithCodeMirror'));

interface SendEmailEditProps {
	item: AppointmentEmail;
	form: FormikProps<AppointmentEmail>;
	loading: boolean;
	error: string;
	withAttachmentButton: boolean;
	readonlyTo: boolean;
}

const SendEmailEdit: React.FC<SendEmailEditProps> = (props) => {
	const {
		item,
		form,
		loading,
		error,
		withAttachmentButton,
		readonlyTo,
	} = props;

	const [withCopies, setWithCopies] = React.useState(false);
	const { companySettings } = useCompanySettingsContext();
	const { checkFeatureAccess } = useCompanyFeatureProviderContext();
	const hasCustomMailBox = checkFeatureAccess(Feature.CustomMailBox) && !!companySettings.sender;
	const [img, setImg] = React.useState<FileInterface | null>(null);
	const emailFeatureIsEnabled = checkFeatureAccess(Feature.Email);

	const sender = form.values.useCustomMailboxConfig
		? companySettings.sender
		: 'support@northerntuna.com';

	const imageIndex = form.values.attachments
		.filter((f) => extensions.includes(f.file?.extension?.toLowerCase()))
		.findIndex((image) => image.file?.id === img?.file?.id);

	return <>
		<div className="clearfix">
			<div
				style={{
					margin: '-6px 0 15px', lineHeight: '34px', display: 'flex', flexDirection: 'row', gap: 20,
				}}
			>
				<div
					style={{
						fontSize: 14, lineHeight: 1.5, flex: '1 1 auto', width: '1px',
					}}
				>
					<strong>Sender:</strong>
					<br />
					<div className="ellipsis">
						<Tooltip title={sender} color="white" placement="topLeft">
							<span>
								{sender}
							</span>
						</Tooltip>
					</div>
				</div>
				<div className="appointment-popup email-popup-menu__edit">
					{hasCustomMailBox && (
						<FormikField.Switch
							fieldName="useCustomMailboxConfig"
							title="Use Custom Mailbox"
							size="small"
							checkedChildren="Custom Mailbox"
							unCheckedChildren="Common Mailbox"
						/>
					)}
				</div>
			</div>
		</div>
		<div
			className="send-email-modal__form custom-scroll send-email-modal__edit"
		>
			{readonlyTo
				? (
					<ReadOnlyControlValue
						className="form-group"
						title="To"
						value={Array.isArray(item.to) ? item.to.join(',') : item.to}
					/>
				)
				: (
					<FormikField
						containerClassName="form-group"
						title="To"
						fieldName="to"
						render={({ form }) => <Select
							mode="tags"
							value={form.values.to}
							style={{ width: '100%' }}
							onChange={(value) => {
								form.setFieldValue('to', value);
							}}
						/>}
					/>
				)
			}
			<div className="clearfix" style={{ marginTop: -7 }}>
				<Switch
					className="pull-right"
					size="small"
					checkedChildren="copies"
					unCheckedChildren="copies"
					checked={withCopies}
					onChange={setWithCopies}
				/>
			</div>
			<div style={{ display: withCopies ? undefined : 'none' }}>
				<FormikField
					containerClassName="form-group"
					title="Copy"
					fieldName="cc"
					render={({ form }) => {
						return <Select
							mode="tags"
							style={{ width: '100%' }}
							value={form.values.cc}
							onChange={(value) => {
								form.setFieldValue('cc', value);
							}}
						/>;
					}}
				/>
				<FormikField
					containerClassName="form-group"
					title="Blind Copy"
					fieldName="bcc"
					render={({ form }) => {
						return <Select
							mode="tags"
							style={{ width: '100%' }}
							value={form.values.bcc}
							onChange={(value) => {
								form.setFieldValue('bcc', value);
							}}
						/>;
					}}
				/>
			</div>
			<FormikField
				containerClassName="form-group"
				title="Subject"
				fieldName="subject"
			/>
			{withAttachmentButton && (
				<div className="clearfix" style={{ height: 0 }}>
					<File
						buttonCaption={<i className="fa fa-paperclip" style={{ margin: 0 }} />}
						asButton
						buttonClassName="btn btn-sm btn-primary pull-right btn-attachments"
						objectId={form.values.patientId!}
						type="patient"
						update={(file) => form.setValues((prev) => ({
							...prev,
							attachments: [
								...prev.attachments, {
									id: -1, objectId: prev?.patientId ?? -1, description: '', file,
								},
							],
						}))}
						fileType={FileType.File}
					/>
				</div>
			)}
			<FormikField
				containerClassName="form-group"
				title="Content"
				fieldName="html"
				render={({ form }) => <Wysiwyg
					options={{ disabled: false }}
					onChange={(value: string) => {
						form.setFieldValue('html', value);
					}}
					customPlugins={['fontsize', 'fontfamily', 'lineheight']}
				/>}
			/>
			<Colorbox
				files={form.values.attachments || []}
				defaultIdx={imageIndex > 0 ? imageIndex : null}
				onCancel={() => setImg(null)}
				zIndex={1002}
			/>
			{!!form.values.attachments.length && (
				<div className="form-group mt10 mb10">
					{form.values.attachments.map((a, i) => {
						const isImage = extensions.includes(a.file?.extension?.toLowerCase());

						return (
							<a
								href={isImage ? undefined : a.file?.src}
								className="attached-file mr10 mb10 clearfix"
								key={a.id}
								download={!isImage}
								{...(isImage ? {} : { target: '_blank', rel: 'noreferrer' })}
								onClick={(e) => {
									if (!isImage) return;
									e.preventDefault();
									e.stopPropagation();
									setImg(a);
								}}
							>
								{isImage ? (
									<div
										className="attached-file__image"
									>
										<img
											className="chat-file-tag-image"
											src={a.file?.src}
											alt={a.file?.name}
										/>
									</div>
								) : <i className="fa fa-file" />}
								&nbsp;
								{a.file?.name}
								{withAttachmentButton && (
									<i
										className="fa fa-times attached-file__remove"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											form.setFieldValue('attachments', removeFromArrayByIndex(form.values.attachments, i));
										}}
									/>
								)}
							</a>
						);
					})}
				</div>
			)}
		</div>
		<div className="clearfix text-center">
			<Button
				isLoading={loading}
				type="submit"
				className={`btn btn-primary ${error ? 'mb10' : ''}`}
				disabled={!emailFeatureIsEnabled}
				title={!emailFeatureIsEnabled ? 'Email feature is disabled' : ''}
			>
				Send
			</Button>
		</div>
	</>;
};

export default SendEmailEdit;
